import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ALWAYS_ON_BY_MODULE } from "@/lib/moduleAccessOverrides";

const MODULE_ROWS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "admission", label: "Admission" },
  { key: "administration", label: "Administration" },
  { key: "academic", label: "Academic" },
  { key: "examination", label: "Examination" },
  { key: "accounting", label: "Accounting" },
  { key: "student_services", label: "Student Services" },
  { key: "discipline", label: "Discipline" },
  { key: "pre_registration", label: "Pre-Registration" },
  { key: "communication", label: "Communication" },
  { key: "user_management", label: "User Management" },
  { key: "audit_log", label: "Audit Log" },
  { key: "attendance", label: "Attendance" },
  { key: "result_visibility", label: "Result Visibility Control" },
];

// HODs cluster: toggling this column affects all 3 roles
const HOD_CLUSTER_ROLES = ["hod_cmt", "hod_pst", "hod_sw"] as const;

interface RoleColumn {
  label: string;
  roles: string[]; // actual app_role values
  isCluster?: boolean;
}

const ROLE_COLUMNS: RoleColumn[] = [
  { label: "Super Admin", roles: ["superadmin"] },
  { label: "Principal", roles: ["principal"] },
  { label: "Deputy Principal", roles: ["deputy_principal"] },
  { label: "Head of Admission", roles: ["head_admission"] },
  { label: "Head of Administration", roles: ["head_administration"] },
  { label: "Head of Academic", roles: ["head_academic"] },
  { label: "Head of Examination", roles: ["head_examination"] },
  { label: "Head of Accounting", roles: ["head_accounting"] },
  { label: "Head of Student Services", roles: ["head_student_service"] },
  { label: "Head of Discipline", roles: ["head_discipline"] },
  { label: "HODs", roles: [...HOD_CLUSTER_ROLES], isCluster: true },
  { label: "IT Incharge", roles: ["it_incharge"] },
  { label: "Auditor", roles: ["auditor"] },
  { label: "Student", roles: ["student"] },
];

interface VisibilityRow {
  id: string;
  module_key: string;
  role: string;
  visible: boolean;
}

export function ModuleVisibilityMatrix() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["module-visibility"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_visibility")
        .select("id, module_key, role, visible");
      if (error) throw error;
      return data as VisibilityRow[];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ moduleKey, roles, visible }: { moduleKey: string; roles: string[]; visible: boolean }) => {
      // Upsert for each role in the group
      for (const role of roles) {
        const existing = rows.find(r => r.module_key === moduleKey && r.role === role);
        if (existing) {
          const { error } = await supabase
            .from("module_visibility")
            .update({ visible })
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("module_visibility")
            .insert({ module_key: moduleKey, role: role as any, visible });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["module-visibility"] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const isVisible = (moduleKey: string, roleCol: RoleColumn): boolean => {
    // For clusters, show ON only if ALL roles in cluster are visible
    return roleCol.roles.every(role => {
      const row = rows.find(r => r.module_key === moduleKey && r.role === role);
      return row ? row.visible : false;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Module Visibility</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Module Visibility</CardTitle>
        <CardDescription>Control which modules each role can see in the sidebar navigation</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full">
          <div className="min-w-[900px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-semibold text-foreground sticky left-0 bg-background z-10 min-w-[140px]">
                    Module
                  </th>
                  {ROLE_COLUMNS.map(col => (
                    <th key={col.label} className="text-center py-3 px-1 font-medium text-muted-foreground text-xs min-w-[80px]">
                      <span className="block leading-tight">{col.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_ROWS.map((mod, idx) => (
                  <tr key={mod.key} className={idx % 2 === 0 ? "bg-muted/30" : ""}>
                    <td className="py-2.5 px-2 font-medium text-foreground sticky left-0 bg-inherit z-10">
                      {mod.label}
                    </td>
                    {ROLE_COLUMNS.map(col => {
                      const alwaysOnList = ALWAYS_ON_BY_MODULE[mod.key] ?? [];
                      const isAlwaysOn = alwaysOnList.length > 0 && col.roles.every(r => alwaysOnList.includes(r));
                      if (isAlwaysOn) {
                        return (
                          <td key={col.label} className="text-center py-2.5 px-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    <Lock className="h-3 w-3" />
                                    Always on
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Built into the role — cannot be disabled.</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                        );
                      }
                      const checked = isVisible(mod.key, col);
                      return (
                        <td key={col.label} className="text-center py-2.5 px-1">
                          <Switch
                            checked={checked}
                            onCheckedChange={(val) =>
                              toggleMutation.mutate({ moduleKey: mod.key, roles: col.roles, visible: val })
                            }
                            disabled={toggleMutation.isPending}
                            className="mx-auto"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
